import {
  Box,
  Grid2,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  LinearProgress,
} from "@mui/material";
import { ExclamationCircleIcon, PencilIcon } from "@heroicons/react/24/solid";
import { useLocation, useNavigate } from "react-router-dom";
import React, { FC, useState } from "react";
import i18next from "i18next";
import moment from "moment";
import { Category } from "../enums/Category";
import { RefundReportTableButtons } from "./RefundReportTableButtons";
import ReportEntryActions from "./molecules/ReportEntryActions";
import ReportActionModal from "./molecules/ReportActionModal";
import { useReportActions } from "../hooks/useReportActions";
import { FilterReportEntries } from "../service/reportEntry";
import { formatCurrency } from "../utils/maskFormatMoney";
import { ViewDocument } from "./atoms/ViewDocument";
import Collaborator from "../@types/Collaborator";
import StatusChip from "./molecules/StatusChip";
import { FilterValues } from "../@types/filter";

interface RefundReportTableProps {
  reportData: FilterReportEntries | undefined;
  filterValues: FilterValues | undefined;
  loggedUser?: Collaborator;
  showCollaboratorColumn: boolean;
  title: string;
  showTotalValue: boolean;
  showExportFilesButtons: boolean;
  showSendToButtons: boolean;
  showAddReportEntryButton: boolean;
  groupByCollaborator: boolean;
  page: number;
  size: number;
  isLoading?: boolean;
  collaborators?: Collaborator[];
  // ← Adicionar estas props
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  onSortChange?: (column: string, direction: "asc" | "desc") => void;
}

export const RefundReportTable: FC<RefundReportTableProps> = ({
  reportData,
  filterValues,
  loggedUser,
  showCollaboratorColumn,
  title,
  showTotalValue,
  showExportFilesButtons,
  showSendToButtons,
  showAddReportEntryButton,
  groupByCollaborator,
  page,
  size,
  isLoading,
  sortColumn,
  sortDirection = "asc",
  collaborators,
  onSortChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openModal, setOpenModal] = useState(false);
  const [action, setAction] = useState("");
  const [payload, setPayload] = useState<{ id: number; refuseReason?: string; approveReason?: string }>({ id: 0 });

  const { onRequestAction } = useReportActions();

  const handleApproveReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPayload({ ...payload, approveReason: e.target.value });
  };

  const handleRefuseReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPayload({ ...payload, refuseReason: e.target.value });
  };

  const handleAction = () => {
    onRequestAction(action, payload, () => {
      setOpenModal(false);
      setAction("");
      setPayload({ id: 0 });
    });
  };

  const handleClose = () => {
    setAction("");
    setPayload({ id: 0 });
  };

  // ← Adicionar função para lidar com ordenação
  const handleSort = (column: string) => {
    if (!onSortChange) return;

    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    onSortChange(column, newDirection);
  };

  // ← Função helper para criar cabeçalho com ordenação
  const createSortableHeader = (column: string, label: string, align?: "left" | "center" | "right") => (
    <TableCell sx={{ fontWeight: "bold", textAlign: align }}>
      <TableSortLabel
        active={sortColumn === column}
        direction={sortColumn === column ? sortDirection : "asc"}
        onClick={() => handleSort(column)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <>
      <ReportActionModal
        isOpen={openModal}
        setIsOpen={setOpenModal}
        onClose={handleClose}
        action={action as "approve" | "refuse" | "approvePerManager" | "sendToApproval"}
        onConfirm={handleAction}
        onRefuseReasonChange={handleRefuseReasonChange}
        onApproveReasonChange={handleApproveReasonChange}
        approveReason={payload.approveReason}
        refuseReason={payload.refuseReason}
      />

      {(showAddReportEntryButton || showAddReportEntryButton || showSendToButtons) && (
        <Grid2 container sx={{ width: "100%", padding: 2 }} alignContent="center">
          <Grid2
            size={{ xs: 12 }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              position: "relative",
              overflow: "visible",
            }}
          >
            <Grid2 container spacing={1}>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  color: "#0C7878",
                  fontSize: "0.875rem",
                }}
              >
                {title}
              </Typography>
            </Grid2>
            <RefundReportTableButtons
              collaborators={collaborators}
              reportData={reportData}
              filterValues={filterValues}
              loggedUser={loggedUser}
              showTotalValue={showTotalValue}
              showExportFilesButtons={showExportFilesButtons}
              showSendToButtons={showSendToButtons}
              showAddReportEntryButton={showAddReportEntryButton}
              page={page}
              size={size}
            />
          </Grid2>
        </Grid2>
      )}

      <TableContainer
        sx={{
          padding: 2,
          alignItems: "center",
          height: "calc(100% - 20px)",
          marginLeft: groupByCollaborator ? "1rem" : "0",
          overflowX: "auto",
          overflowY: "auto",
          "& .MuiTable-root": {
            minWidth: 650,
            width: "100%",
          },
        }}
        component={Paper}
      >
        {isLoading && <LinearProgress />}
        {/* Desktop view (table) */}
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {showCollaboratorColumn && createSortableHeader("collaborator", "Colaborador")}
              {createSortableHeader("costCenter.client", "Cliente")}
              {loggedUser?.financialSupport && createSortableHeader("costCenter.owner.name", "Gestor")}
              {createSortableHeader("expenseDate", "Data")}
              {createSortableHeader("value", "Valor")}
              {createSortableHeader("expendCategory", "Categoria")}
              {createSortableHeader("reportEntryStatus", "Status")}
              {createSortableHeader("ownerApprovedDate", "Aprovação pelo gestor")}
              {createSortableHeader("financialApprovedDate", "Aprovação financeira")}
              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Ações</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>*</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!filterValues?.collaborator?.id && location.pathname.includes("/approvals") ? (
              <TableRow>
                <TableCell
                  colSpan={12}
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {/* <SelectCollaboratorChip /> */}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {reportData?.content.map((data) => (
                  <TableRow
                    key={data.id}
                    sx={{
                      backgroundColor: "#FAFAFA",
                      padding: 10,
                    }}
                  >
                    {showCollaboratorColumn && (
                      <TableCell>
                        <Typography variant="body2">{data.collaborator?.name}</Typography>
                      </TableCell>
                    )}

                    <TableCell>
                      <Typography variant="body2">
                        <div className="text-start break-words">
                          <Tooltip title={data.costCenter.name}>
                            <div className="max-w-xs truncate" style={{ maxWidth: "20ch" }}>
                              {data.costCenter.client}
                            </div>
                          </Tooltip>

                          <Tooltip title={data.costCenter.name}>
                            <div className="max-w-xs truncate text-xs" style={{ maxWidth: "30ch" }}>
                              {data.costCenter.name}
                            </div>
                          </Tooltip>
                        </div>
                      </Typography>
                    </TableCell>

                    {loggedUser?.financialSupport && (
                      <TableCell>
                        <Typography variant="body2">{data.costCenter.owner}</Typography>
                      </TableCell>
                    )}

                    <TableCell>
                      <Typography variant="body2">{moment(data.expenseDate).format("DD/MM/YYYY")}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          fontWeight: data.expendCategory === Category.advance ? "bold" : "normal",
                        }}
                      >
                        {formatCurrency(Number(data.value), data.expendCategory === Category.advance)}
                        {data.warnings != null && data.warnings.length > 0 && (
                          <Tooltip
                            title={data.warnings?.map((warning, index) => (
                              <span key={index}>
                                {warning}
                                <br />
                              </span>
                            ))}
                            placement="top"
                            style={{ fontSize: "12px", marginLeft: "8px" }}
                          >
                            <ExclamationCircleIcon className="h-5 w-5 tablet-max:h-6 tablet-max:w-6 text-red-500" />
                          </Tooltip>
                        )}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        <div className="text-start break-word">
                          <div
                            className={`max-w-xs truncate ${
                              data.expendCategory === Category.advance ? "font-bold" : ""
                            }`}
                          >
                            {i18next.t(`category.${data.expendCategory}`)}
                          </div>

                          <div className="max-w-xs truncate text-xs">
                            {data.expendSubcategory ? i18next.t(`sub_category.${data.expendSubcategory}`) : ""}
                          </div>
                        </div>
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <StatusChip data={data} />
                    </TableCell>

                    <TableCell>
                      {data.ownerApprovedDate && (
                        <Typography variant="body2">
                          {Array.isArray(data.ownerApprovedDate)
                            ? moment([
                                data.ownerApprovedDate[0],
                                data.ownerApprovedDate[1] - 1,
                                data.ownerApprovedDate[2],
                              ]).format("DD/MM/YYYY")
                            : moment(data.ownerApprovedDate).format("DD/MM/YYYY")}
                        </Typography>
                      )}
                      {!data.ownerApprovedDate && <Typography variant="body2">-</Typography>}
                    </TableCell>

                    <TableCell>
                      {data.financialApprovedDate && (
                        <Typography variant="body2">
                          {Array.isArray(data.financialApprovedDate)
                            ? moment([
                                data.financialApprovedDate[0],
                                data.financialApprovedDate[1] - 1,
                                data.financialApprovedDate[2],
                              ]).format("DD/MM/YYYY")
                            : moment(data.financialApprovedDate).format("DD/MM/YYYY")}
                        </Typography>
                      )}
                      {!data.financialApprovedDate && <Typography variant="body2">-</Typography>}
                    </TableCell>

                    <TableCell>
                      <ReportEntryActions
                        data={data}
                        loggedUser={loggedUser}
                        onApprove={() => {
                          setAction("approve");
                          setPayload({ id: data.id, approveReason: data.financialApprovedReason || "" });
                          onRequestAction(
                            "approve",
                            { id: data.id, approveReason: data.financialApprovedReason || "" },
                            () => {
                              setOpenModal(false);
                              setAction("");
                              setPayload({ id: 0 });
                            },
                          );
                        }}
                        onApprovePerManager={() => {
                          setAction("approvePerManager");
                          setPayload({ id: data.id, approveReason: data.ownerApprovedReason || "" });
                          setOpenModal(true);
                        }}
                        onRefuse={() => {
                          setAction("refuse");
                          setPayload({ id: data.id });
                          setOpenModal(true);
                        }}
                        onReturnToApproval={() => {
                          setAction("sendToApproval");
                          setPayload({ id: data.id });
                          setOpenModal(true);
                        }}
                        onEdit={() => {
                          if (data.expendCategory === Category.advance) {
                            navigate(`/edit_advance_payment?id=${data.id}`);
                          } else {
                            navigate(`/edit_request?id=${data.id}`);
                          }
                        }}
                        onDelete={() => {
                          // TODO: Implementar ação de deletar
                        }}
                        onMarkAsPaid={() => {
                          setAction("markAsPaid");
                          setPayload({ id: data.id });
                          setOpenModal(true);
                        }}
                        onConfirmReceipt={() => {
                          navigate(`/confirm_advance_receipt?id=${data.id}`);
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          gap: 2,
                          alignItems: "center",
                        }}
                      >
                        <ViewDocument reportEntry={data} />

                        <Tooltip
                          title={
                            data.expendCategory === Category.advance ? "Editar Adiantamento" : "Editar Solicitação"
                          }
                          onClick={() => {
                            if (data.expendCategory === Category.advance) {
                              navigate(`/edit_advance_payment?id=${data.id}`);
                            } else {
                              navigate(`/edit_request?id=${data.id}`);
                            }
                          }}
                        >
                          <PencilIcon fill="#6B7280" className="w-5 h-5 text-blue-500 cursor-pointer" />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
